<template>
  <div class="wagers-container">
    <!-- Desktop Table View -->
    <div class="wagers-card desktop-only">
      <div class="table-container">
        <table class="wagers-table">
          <thead>
            <tr>
              <th>{{ $t('wagers.date') }}</th>
              <th>{{ $t('wagers.transId') }}</th>
              <th>{{ $t('wagers.drawsPlayed') }}</th>
              <th>{{ $t('wagers.description') }}</th>
              <th>{{ $t('wagers.totalBetAmount') }}</th>
              <th class="total-return-header">
                {{ $t('wagers.totalReturn') }}
              </th>
              <th>{{ $t('wagers.status') }}</th>
              <th>{{ $t('wagers.detail') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading && tableRows.length === 0">
              <td
                colspan="8"
                class="loader-cell"
              >
                <div class="table-loader">
                  <span class="spinner" />
                  {{ $t('wagers.loadingTransactions') }}
                </div>
              </td>
            </tr>
            <template v-else-if="tableRows.length === 0">
              <tr>
                <td
                  colspan="8"
                  class="empty-cell"
                >
                  {{ $t('wagers.noTransactions') }}
                </td>
              </tr>
            </template>
            <template v-else>
              <template
                v-for="item in tableRows"
                :key="item.id"
              >
                <tr class="transaction-row">
                  <td>{{ formatDate(item.created_at) }}</td>
                  <td>{{ item.id }}</td>
                  <td>{{ getDrawsPlayed(item) }}</td>
                  <td>{{ $t('wagers.wager') }}</td>
                  <td>{{ item.currency_symbol }} {{ item.total }}</td>
                  <td>{{ getTotalReturn(item) }}</td>
                  <td>
                    <span :class="['status', formatStatusClass(item), getStatusClass(item)]">
                      {{ formatStatusLabel(item) }}
                    </span>
                  </td>
                  <td class="detail-cell">
                    <button
                      class="details-icon-button"
                      :title="$t('wagers.detail')"
                      @click.stop="$emit('view-details', item)"
                    >
                      <img
                        src="/img/icons/info.svg"
                        alt="Details"
                        class="info-icon"
                      >
                    </button>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile Card View -->
    <div
      class="mobile-cards mobile-only"
      :class="{ 'mobile-cards--refreshing': loading && tableRows.length > 0 }"
    >
      <div
        v-if="loading && tableRows.length === 0"
        class="loading-cards"
      >
        <div class="card-loader">
          <span class="spinner" />
          {{ $t('wagers.loadingTransactions') }}
        </div>
      </div>
      <template v-else-if="tableRows.length === 0">
        <div class="empty-cards">
          <p>{{ $t('wagers.noTransactions') }}</p>
        </div>
      </template>
      <template v-else>
        <WagerCard
          v-for="item in tableRows"
          :key="item.id"
          :transaction="item"
          @view-details="$emit('view-details', $event)"
          @rebet="handleRebet"
          @copy-success="$emit('copy-success')"
          @copy-failed="$emit('copy-failed')"
        />
      </template>
      <div
        v-if="loading && tableRows.length > 0"
        class="mobile-cards-loading-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="card-loader">
          <span class="spinner" />
          {{ $t('wagers.loadingTransactions') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Transaction } from '@/stores/transactions'
import WagerCard from './WagerCard.vue'
import { formatCurrency } from '@/utils/currency'
import { useTheme } from '@/composables/useTheme'

const props = defineProps<{
  transactions: Transaction[]
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'view-details', wager: Transaction): void
  (e: 'rebet'): void
  (e: 'copy-success'): void
  (e: 'copy-failed'): void
}>()

const { t } = useI18n()
const { currentThemeName } = useTheme()
const useWonLostLabels = computed(() => currentThemeName.value === 'wsb')

const formatStatusLabel = (item: Transaction) => {
  const status = item.bet_status
  if (!useWonLostLabels.value || String(status).toLowerCase() !== 'settled') {
    return status
  }
  return parseFloat(String(item.total_winnings || 0)) > 0 ? t('wagers.won') : t('wagers.lost')
}

const formatStatusClass = (item: Transaction) => {
  const status = item.bet_status
  if (!useWonLostLabels.value || String(status).toLowerCase() !== 'settled') {
    return String(status).toLowerCase()
  }
  return parseFloat(String(item.total_winnings || 0)) > 0 ? 'won' : 'lost'
}

const tableRows = computed(() => {
  return props.transactions
})

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const handleRebet = () => {
  emit('rebet')
}

const getDrawsPlayed = (item: Transaction) => {
  const count = item.draws_played
  if (count === undefined || count === null) {
    return '—'
  }
  return count
}

const getTotalReturn = (item: Transaction) => {
  if (item.bet_status.toLowerCase() === 'open') {
    return t('wagers.tbc')
  }
  return formatCurrency(item.total_winnings, item.currency_symbol)
}

const getStatusClass = (item: Transaction) => {
  if (item.bet_status.toLowerCase() === 'settled' && item.total_winnings === '0.00') {
    return 'settled-zero'
  }
  return ''
}
</script>

<style scoped>
.wagers-container {
  width: 100%;
}

.wagers-card {
  background: var(--colour-surface-cont-lowest);
  flex: 1;
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--colour-surface-cont);
  border-radius: var(--wagers-card-radius, 0);
  box-shadow: var(--wagers-card-shadow, none);
  box-sizing: border-box;
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

.mobile-cards {
  padding: 0 10px 10px;
  position: relative;
}

.mobile-cards--refreshing {
  pointer-events: none;
}

.mobile-cards-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--colour-surface-cont-lowest) 82%, transparent);
}

.loading-cards,
.empty-cards {
  text-align: center;
  padding: 40px 20px;
  color: var(--colour-base-text-colour);
}

.card-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.mobile-details {
  margin-top: 16px;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.table-container::-webkit-scrollbar {
  display: none;
}

.wagers-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 100%;
  margin: 0 auto;
}

.wagers-table th {
  background: var(--wagers-table-header-bg, var(--wagers-table-body-bg, var(--colour-surface-cont-lowest)));
  color: var(--wagers-table-header-color, var(--colour-base-text-colour));
  font-weight: 600;
  text-align: center;
  padding: 16px;
  font-size: 14px;
  border-bottom: 1px solid var(--colour-surface-cont);
}

.wagers-table td {
  background: var(--wagers-table-body-bg, var(--bg-table-body, var(--colour-surface-cont-lowest)));
  padding: 16px;
  color: var(--colour-base-text-colour);
  font-size: 14px;
  border-bottom: 1px solid var(--colour-surface-cont);
  text-align: center;
}

.wagers-table tr:last-child td {
  border-bottom: none;
}

.wagers-table tr:hover {
  background: var(--colour-surface-cont);
}

.transaction-row {
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  align-items: center;
}

.transaction-row:hover {
  background: var(--colour-surface-cont);
  border-left-color: var(--colour-orange-button);
}

.transaction-row.expanded {
  background: var(--colour-surface-cont);
  /* border-left-color: var(--colour-orange-button); */
  box-shadow: 0 2px 8px var(--shadow-color);
}

.status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: inherit;
  font-weight: 500;
}

.status.open {
  color: var(--colour-yellow-text-colour);
}

.status.settled,
.status.won {
  color: var(--colour-green-button);
}

.status.lost {
  color: var(--colour-red-text-colour);
}

.status.cancelled {
  color: var(--colour-red-text-colour);
}

.status.settled-zero {
  color: var(--colour-red-text-colour);
}

.wagers-table .detail-cell {
  text-align: center;
  vertical-align: middle;
}

.details-icon-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  transition: opacity 0.2s;
}

.details-icon-button:hover {
  opacity: 0.7;
}

.info-icon {
  width: 20px;
  height: 20px;
  filter: var(--icon-filter, none);
}

.total-return-header {
  color: var(--wagers-table-header-color, var(--colour-base-text-colour));
}

.details-row {
  background: var(--colour-surface-cont);
}

.details-cell {
  padding: 0 !important;
  background: var(--colour-surface-cont);
}

.transaction-details-container {
  background: var(--colour-surface-cont-lowest);
  border-radius: 12px;
  border: 1px solid var(--colour-surface-cont);
  box-shadow: 0 4px 12px var(--shadow-color);
  overflow: hidden;
}

.loader-cell {
  text-align: center;
  padding: 40px 0;
  color: var(--colour-base-text-colour);
  font-size: 16px;
}

.table-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 4px solid var(--colour-surface-cont);
  border-top: 4px solid var(--colour-orange-button);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-cell {
  text-align: center;
  color: var(--colour-secondary-text-colour);
  font-size: 16px;
  padding: 40px 0;
}

/* Large screen improvements */
@media (min-width: 1200px) {
  .transaction-details-container {
    border-radius: 16px;
    box-shadow: 0 8px 24px var(--shadow-color);
  }
  
  .wagers-table {
    min-width: 1000px;
  }
  
  .wagers-table th,
  .wagers-table td {
    padding: 20px 16px;
    font-size: 15px;
  }
  
  .transaction-row.expanded {
    box-shadow: 0 4px 16px var(--shadow-color);
  }
}

@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: block;
  }

  .wagers-table th,
  .wagers-table td {
    padding: 12px;
    font-size: 13px;
    white-space: nowrap;
  }

  .status {
    padding: 3px 6px;
    font-size: inherit;
  }

  .details-icon-button {
    padding: 2px;
  }
  
  .info-icon {
    width: 18px;
    height: 18px;
  }
  
  .transaction-details-container {
    margin: 8px;
    border-radius: 8px;
  }
}
</style> 
